import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/testIds";
import { Menu, X } from "lucide-react";
import { useT } from "@/lib/i18n";
import Wordmark from "@/components/sentinel/Wordmark";
import LanguageSwitcher from "@/components/sentinel/LanguageSwitcher";

const LOGO_URL =
    "https://customer-assets-jt897jd0.emergentagent.net/job_417ef979-c5a1-480b-a52d-ba174268846c/artifacts/hbgmknso_669BBB83-039C-4BBD-AA28-5D92E2D6BCAC.png";

export default function Nav() {
    const { t } = useT();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { id: "problema", label: t("problem.eyebrow"), tid: "sn-nav-link-problema" },
        { id: "come-funziona", label: t("nav.comeFunziona"), tid: TID.nav.linkComeFunziona },
        { id: "ricompense", label: t("nav.ricompense"), tid: "sn-nav-link-ricompense" },
        { id: "roadmap", label: t("roadmap.eyebrow"), tid: "sn-nav-link-roadmap" },
        { id: "faq", label: t("nav.faq"), tid: TID.nav.linkFaq },
        { id: "sostieni", label: t("nav.sostieni"), tid: "sn-nav-link-sostieni" },
    ];

    return (
        <motion.header
            data-testid={TID.nav.root}
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,padding] duration-500 ${
                scrolled
                    ? "bg-[rgba(8,8,12,0.75)] backdrop-blur-xl border-b border-white/5 py-3"
                    : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid={TID.nav.logo}
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={LOGO_URL}
                        alt="Sentinel"
                        className="w-8 h-8 object-contain"
                    />
                    <Wordmark size="text-lg" />
                </a>

                <nav className="hidden md:flex items-center gap-8 lg:gap-10">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={l.tid}
                            className="font-mono-e text-[11px] uppercase tracking-[0.22em] text-sn-fog hover:text-sn-silver transition-colors duration-300"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <LanguageSwitcher variant="desktop" />
                    <a
                        href="#waitlist"
                        data-testid={TID.nav.cta}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-sn-silver btn-sweep btn-sweep-dark font-mono-e text-[11px] uppercase tracking-[0.22em] transition-colors duration-300"
                    >
                        {t("nav.cta")}
                        <span className="text-sn-blue">→</span>
                    </a>
                </div>

                <div className="md:hidden flex items-center gap-2">
                    <LanguageSwitcher variant="mobile" />
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid={TID.nav.mobileToggle}
                        className="text-sn-silver p-2"
                        aria-label="menu"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden bg-[rgba(8,8,12,0.95)] backdrop-blur-xl border-t border-white/5 overflow-hidden"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) => (
                                <a
                                    key={l.id}
                                    href={`#${l.id}`}
                                    data-testid={l.tid}
                                    onClick={() => setOpen(false)}
                                    className="font-mono-e text-xs uppercase tracking-[0.22em] text-sn-fog"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#waitlist"
                                data-testid={TID.nav.cta}
                                onClick={() => setOpen(false)}
                                className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-sn-silver font-mono-e text-[11px] uppercase tracking-[0.22em]"
                            >
                                {t("nav.cta")} →
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
